import React, {
	ChangeEvent, ClipboardEvent, FC, KeyboardEvent, useEffect, useMemo,
} from 'react';
import {
	AsyncCellSearch, ICellEditor,
} from '@aleph/shared-components';
import {
	InterestOptionsQuery,
	RedditInterestTargetingLabelsQuery,
	RedditInterestTargetingLabelsQueryVariables,
} from 'wb-reddit/graphql/generated';
import { useLazyQuery } from '@apollo/client';
import redditInterestLabelsQuery from 'wb-reddit/app/routes/superGrid/targeting/api/interestLabels.graphql';
import { uniqBy } from 'lodash';

const InterestsCellEditor: FC<ICellEditor<InterestOptionsQuery['options']['items']>> = (
	params,
) => {
	const initialValue = useMemo(() => params.value, []);
	const {
		data, value, setValue, editorContext, setEditorContext,
	} = params;

	const [getInterestLabels, { loading }] = useLazyQuery<RedditInterestTargetingLabelsQuery, RedditInterestTargetingLabelsQueryVariables>(redditInterestLabelsQuery);

	const changeSearch = (e: ChangeEvent<HTMLInputElement>) => {
		setEditorContext({
			...editorContext,
			search: e.target.value,
		});
	};

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			params.stopEditing();
		}

		if (e.key === 'Escape') {
			setValue(initialValue);

			setTimeout(() => params.stopEditing());
		}
	};

	useEffect(() => {
		setEditorContext({
			...editorContext,
			adAccountId: data?.parent?.parent?.adAccount?.adAccountId,
		});
	}, [data]);

	const onPaste = async (e: ClipboardEvent) => {
		e.stopPropagation();
		e.preventDefault();

		const interestIds = e?.clipboardData?.getData?.('text')?.split?.('\n')?.filter?.(text => !!text) ?? [];

		try {
			const { data: interestsData } = await getInterestLabels({
				variables: {
					interestIds,
				},
			});

			setValue(
				uniqBy(
					[
						...(value ?? []),
						...(interestsData?.targetingInterests?.items ?? []),
					] as InterestOptionsQuery['options']['items'],
					'value',
				),
			);
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<AsyncCellSearch onChange={changeSearch}
			onKeyDown={onKeyDown}
			onPaste={onPaste}
			value={editorContext?.search ?? ''}
			loading={loading}
			loaderText='Pasting...'
			placeholder='Search interests...'
			onSubmit={() => params.stopEditing()}
			onCancel={() => params.api.stopEditing(true)}
		/>
	);
};

export default InterestsCellEditor;
